import invariant from 'invariant';
import fromPairs from 'lodash/fromPairs';

import { DisclosureTargetIntensityType } from '@watershed/constants/reductions';
import { TimeseriesFrequency } from '@watershed/constants/timeseries';
// eslint-disable-next-line no-restricted-imports
import { GQDisclosureForSuppliersTableFragment } from '../generated/graphql';
// eslint-disable-next-line no-restricted-imports
import {
  GQCompanyClimateCommitment,
  GQExternalReportType,
  GQPrivateDisclosureType,
  GQSbtCommitmentStage,
} from '../generated/graphql-schema-types';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';

import Company from '../companyData/Company';
import {
  getFootprintScopeFromFilterGroupExpression,
  getReadableScopeFromFilterGroupExpression,
  getTargetName,
} from '../companySurveys/utils';
import { FootprintScope } from '../forecast/types';
import { isScienceBasedTargetCommitment } from '../utils/climateCommitmentUtils';
import {
  getDisclosureType,
  isSurveyDisclosure,
} from '../utils/DisclosureUtils';
import {
  generateDescriptionForParentTargetLegacy,
  inferTimeseriesInfo,
} from '../utils/PlanTargetUtils';
import { FiscalYearlyPercentageTimeseries } from '../utils/SimpleTimeseries';
import { supplierDisclosureTargetToPlanTarget } from '../utils/TargetUtils';
import { YearMonth, YM } from '../utils/YearMonth';

export interface CombinedTarget {
  id: string;
  parentId: string | null;
  parentName: string | null;
  createdAt: YearMonth;
  baselineYear: number | null;
  targetDate: YearMonth | null;
  targetYear: number | null;
  title: string;
  description: string | null;
  scopes: Array<FootprintScope>;
  reductionPercentage: number | null;
  sbtiStatus?: string | null;
  externalUrl?: string | null;
  source: string;
  disclosureType: GQExternalReportType | GQPrivateDisclosureType | null;
  intensityType: DisclosureTargetIntensityType | null;
}

// adapted from SupplierDetailsTabDataHistory
function getFieldsFromDisclosure(
  disclosure: GQDisclosureForSuppliersTableFragment
): Pick<CombinedTarget, 'source' | 'disclosureType' | 'createdAt'> {
  const disclosureType = getDisclosureType(disclosure);
  if (isNotNullish(disclosure.publicDisclosure)) {
    return {
      source: `${disclosure.publicDisclosure?.reportType} climate report ${disclosure.publicDisclosure.publishingYear}`,
      disclosureType,
      createdAt: YM.make(disclosure.publicDisclosure.publishingYear),
    };
  }

  invariant(
    isNotNullish(disclosure.privateDisclosure),
    // this is enforced by DB constraint
    'Public disclosure and private disclosure must not both be null'
  );

  let source = 'Private data';
  if (isSurveyDisclosure(disclosure.privateDisclosure.privateDisclosureType)) {
    source = `Watershed survey response ${disclosure.privateDisclosure.createdAt.getFullYear()}`;
  } else if (
    disclosure.privateDisclosure.privateDisclosureType ===
    GQPrivateDisclosureType.EmitwiseSupplier
  ) {
    source = `Supplier data imported from Emitwise ${disclosure.privateDisclosure.createdAt.getFullYear()}`;
  }

  return {
    source,
    disclosureType,
    createdAt: YM.fromJSDate(disclosure.privateDisclosure.createdAt),
  };
}

function getSbtiStatusFromCommitment(commitment: GQCompanyClimateCommitment) {
  if (
    isScienceBasedTargetCommitment(commitment) &&
    'commitment' in commitment
  ) {
    if (commitment.commitment.stage === GQSbtCommitmentStage.Committed) {
      return 'Committed';
    } else if (
      commitment.commitment.stage === GQSbtCommitmentStage.TargetsSet
    ) {
      if (commitment.submittedToSBTi) {
        return 'Validated';
      } else {
        return 'Set';
      }
    } else if (commitment.commitment.stage === GQSbtCommitmentStage.Removed) {
      return 'Removed';
    }
  }
  return null;
}

export function getCombinedTargetFromTarget(
  disclosure: GQDisclosureForSuppliersTableFragment,
  climateCommitmentDisclosures: Array<GQDisclosureForSuppliersTableFragment>,
  name: string,
  parentFields?: {
    parentId: string;
    parentName: string;
  }
): Array<CombinedTarget> {
  const companyClimateCommitmentIdToSbtiCommitment: Record<
    string,
    GQCompanyClimateCommitment | undefined
  > = fromPairs(
    climateCommitmentDisclosures.flatMap((disclosure) =>
      (disclosure.climateCommitments ?? []).map((commitment) => [
        commitment.id,
        commitment,
      ])
    )
  );

  return (
    disclosure.targets?.map((target) => {
      const scopes = getFootprintScopeFromFilterGroupExpression(target.filters);
      const commitment = target.companyClimateCommitmentId
        ? companyClimateCommitmentIdToSbtiCommitment[
            target.companyClimateCommitmentId
          ]
        : null;

      const tmInfo =
        target.emissionsTarget &&
        target.emissionsTarget.frequency === TimeseriesFrequency.Yearly
          ? inferTimeseriesInfo(
              FiscalYearlyPercentageTimeseries.fromNonUTCTimeZone(
                target.emissionsTarget
              )
            )
          : null;

      return {
        id: target.id,
        base: target.emissionsTarget?.base ?? null,
        parentId: parentFields?.parentId ?? null,
        parentName: parentFields?.parentName ?? null,
        targetDate: tmInfo?.targetEndYear ?? null,
        targetYear: tmInfo?.targetEndYear
          ? YM.year(tmInfo.targetEndYear)
          : null,
        baselineYear: YM.year(target.baseYear),
        title: getTargetName(
          target.name,
          target.baseYear,
          getReadableScopeFromFilterGroupExpression(target.filters)
        ),
        description:
          target.description ??
          generateDescriptionForParentTargetLegacy(
            supplierDisclosureTargetToPlanTarget(target),
            // Hack for the `Unit` intensity type
            target.intensityType === DisclosureTargetIntensityType.Unit
              ? target.unit
              : null,
            parentFields?.parentName ?? name
          ),
        scopes,
        reductionPercentage: tmInfo?.percentChange
          ? tmInfo?.percentChange / 100
          : null,
        sbtiStatus: commitment ? getSbtiStatusFromCommitment(commitment) : null,
        intensityType: target.intensityType ?? null,
        ...getFieldsFromDisclosure(disclosure),
      };
    }) ?? []
  );
}

export function getCombinedTargetFromCommitment(
  disclosure: GQDisclosureForSuppliersTableFragment,
  parentFields?: {
    parentId: string;
    parentName: string;
  }
): Array<CombinedTarget> {
  return (
    disclosure.climateCommitments?.map((commitment) => {
      let targetDate = null;
      if ('targetYear' in commitment && commitment.targetYear) {
        targetDate = YM.make(commitment.targetYear);
      } else if (commitment.commitmentPeriodEnd) {
        targetDate = YM.fromJSDate(commitment.commitmentPeriodEnd);
      }

      return {
        id: commitment.id,
        parentId: parentFields?.parentId ?? null,
        parentName: parentFields?.parentName ?? null,
        targetDate,
        targetYear: targetDate ? YM.year(targetDate) : null,
        baselineYear:
          commitment.__typename === 'ScienceBasedTargetCommitment'
            ? commitment.commitment?.baselineYear
            : null,
        title: Company.getCommitmentTitle(commitment),
        scopes: [],
        description: commitment.description,
        reductionPercentage:
          'targetPercentageCleanEnergy' in commitment
            ? commitment.targetPercentageCleanEnergy
              ? commitment.targetPercentageCleanEnergy / 100
              : null
            : null,
        externalUrl: disclosure.publicDisclosure?.publicUrl,
        sbtiStatus: getSbtiStatusFromCommitment(commitment),
        intensityType: null, // commitments do not have intensity type
        ...getFieldsFromDisclosure(disclosure),
      };
    }) ?? []
  );
}
